import { subDays } from "date-fns";
import equal from "fast-deep-equal/es6";
import { computed } from "mobx";

import { OrganizationClient } from "@formant/core/client/OrganizationClient";
import { defined } from "@formant/core/common/defined";
import { CognitoRegion } from "@formant/core/model/CognitoRegion";
import { duration } from "@formant/core/model/duration";
import { IChangePlanRequest } from "@formant/core/model/IChangePlanRequest";
import { IOrganization } from "@formant/core/model/IOrganization";
import { IStripeInvoice } from "@formant/core/model/IStripeInvoice";
import { IStripeToken } from "@formant/core/model/IStripeToken";
import {
    ITeleopHighPingReconnectBehaviors,
    teleopHighPingReconnectBehaviorsDefaults,
} from "@formant/core/model/ITeleopHighPingReconnectBehaviors";
import { IUsageRecord } from "@formant/core/model/IUsageRecord";
import { IUsageRecordQuery } from "@formant/core/model/IUsageRecordQuery";
import { IUser } from "@formant/core/model/IUser";
import { OrganizationFeatureFlag } from "@formant/core/model/OrganizationFeatureFlag";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { IOrganizationStore } from "./IOrganizationStore";
import { StoreCache } from "./StoreCache";

export class OrganizationStore implements IOrganizationStore {
    private listCache = new StoreCache<undefined, IOrganization[]>();
    private userListCache = new StoreCache<Uuid, IUser[]>();
    private cache = new StoreCache<Uuid, IOrganization | false>();
    private usageRecordCache = new StoreCache<
        IUsageRecordQuery,
        IUsageRecord[] | false
    >();
    private invoicesCache = new StoreCache<Uuid, IStripeInvoice[] | false>({
        staleIntervalMs: 10 * duration.minute,
    });

    constructor(
        private organizationClient: OrganizationClient,
        private authStore: IAuthStore,
    ) {}

    isSelfServe(): boolean {
        const org = this.authenticated();
        return org ? org.plan === "self-serve" : false;
    }

    get(id: Uuid): IOrganization | false | undefined {
        return this.cache.get(id, async () => await this.fetchGet(id));
    }

    list(): IOrganization[] | undefined {
        return this.listCache.get(undefined, async () =>
            this.organizationClient.list(await this.authStore.token()),
        );
    }

    listUsers(id: Uuid): IUser[] | undefined {
        return this.userListCache.get(
            id,
            async () =>
                await this.organizationClient.listUsers(
                    await this.authStore.token(),
                    id,
                ),
        );
    }

    authenticated(): IOrganization | false | undefined {
        const id = this.authStore.organizationId;
        if (!id) {
            return false;
        }
        return this.get(id);
    }

    async authenticatedAsync(): Promise<IOrganization | false | undefined> {
        const id = this.authStore.organizationId;
        if (!id) {
            return false;
        }
        return this.fetchGet(id);
    }

    get defaultStreamThrottleHz() {
        return (
            (this.authenticated() || undefined)?.defaultStreamThrottleHz ?? 5
        );
    }

    get onlineSensitivity() {
        return (this.authenticated() || undefined)?.onlineSensitivity ?? 10;
    }

    async update(id: Uuid, organization: Partial<IOrganization>) {
        const updatedValue = await this.organizationClient.update(
            await this.authStore.token(),
            id,
            organization,
        );

        const value = updatedValue || false;
        this.cache.set(id, value);
    }

    async fetchGet(id: Uuid): Promise<IOrganization | false> {
        const organization = await this.organizationClient.get(
            await this.authStore.token(),
            id,
        );
        const value = organization || false;
        this.cache.set(id, value);
        return value;
    }

    async changePlan(id: Uuid, request: IChangePlanRequest) {
        await this.organizationClient.changePlan(
            await this.authStore.token(),
            id,
            request,
        );
        await this.fetchGet(id);
    }

    async putStripeToken(id: Uuid, stripeToken: IStripeToken) {
        await this.organizationClient.putStripeToken(
            await this.authStore.token(),
            id,
            stripeToken,
        );
        await this.fetchGet(id);
    }

    // TODO this is effectively a duplicate of `authenticated()` and only used internally
    private get authenticatedOrganization(): IOrganization | undefined {
        const { organizationId } = this.authStore;
        return (organizationId && this.get(organizationId)) || undefined;
    }

    get isGoogleSheetsEnabled(): boolean {
        const organization = this.authenticatedOrganization;
        return !!organization?.googleInfo;
    }

    get isStatefulEventsEnabled(): boolean {
        const organization = this.authenticatedOrganization;
        return !!organization?.statefulEventsEnabled;
    }

    get googleIntegrationEnabled() {
        return !!defined(
            this.get(defined(this.authStore.organizationId)) || undefined,
        ).googleInfo;
    }

    get demoModeEnabled() {
        return !!defined(
            this.get(defined(this.authStore.organizationId)) || undefined,
        ).demoModeEnabled;
    }

    get teleopShareEnabled() {
        return !!defined(
            this.get(defined(this.authStore.organizationId)) || undefined,
        ).teleopShareEnabled;
    }

    get overviewSettingsEnabled() {
        const organization = this.authenticatedOrganization;
        return (
            organization &&
            organization.overviewSettings &&
            organization.overviewSettings.overviewSettingsEnabled
        );
    }

    getFeatureFlags(): Set<OrganizationFeatureFlag> {
        return new Set(this.authenticatedOrganization?.flags || []);
    }

    queryUsageRecords(query: IUsageRecordQuery) {
        return this.usageRecordCache.get(
            query,
            async () =>
                await this.organizationClient.queryUsageRecords(
                    await this.authStore.token(),
                    query,
                ),
        );
    }

    getStripeInvoices(organizationId: Uuid) {
        return this.invoicesCache.get(
            organizationId,
            async () =>
                (await this.organizationClient.getStripeInvoices(
                    await this.authStore.token(),
                    organizationId,
                )) || false,
        );
    }

    isBetaUIEnabled(): boolean {
        const org = this.authenticatedOrganization;
        return (org && org.betaUI) || false;
    }

    isRealtimeV2Enabled(): boolean {
        const org = this.authenticatedOrganization;
        return (org && org.realtimeV2Enabled) || false;
    }

    isWhiteLabelEnabled(): boolean {
        const org = this.authenticatedOrganization;
        return (org && org.whiteLabelEnabled) || false;
    }

    getWhiteLabelStyle(): string {
        const organization = this.authenticatedOrganization;
        if (organization && organization.whiteLabelEnabled) {
            return organization.whiteLabelCSS || "";
        }
        return "";
    }

    dataExportEnabled(): boolean {
        return !!this.authenticatedOrganization?.dataExportEnabled;
    }

    s3ExportEnabled(): boolean {
        return !!this.authenticatedOrganization?.s3ExportEnabled;
    }

    googleStorageExportEnabled(): boolean {
        return !!this.authenticatedOrganization?.googleStorageExportEnabled;
    }

    advancedConfigurationEnabled(): boolean {
        return !!this.authenticatedOrganization?.advancedConfigurationEnabled;
    }

    outageBannerEnabled(): boolean {
        return !!this.authenticatedOrganization?.outageBannerEnabled;
    }

    newHeaderEnabled(): boolean {
        return !!this.authenticatedOrganization?.newHeaderEnabled;
    }

    explorationsEnabled(): boolean {
        return !!this.authenticatedOrganization?.explorationsEnabled;
    }

    inactivityLogoutEnabled(): boolean {
        return !!this.authenticatedOrganization?.inactivityLogoutEnabled;
    }

    get inactivityTimeout(): number | undefined {
        return this.authenticatedOrganization?.inactivityTimeout;
    }

    public isMissionPlanningEnabled(): boolean {
        return !!this.authenticatedOrganization?.missionPlanningEnabled;
    }

    getTeleopLatencySettings(): ITeleopHighPingReconnectBehaviors {
        const settings =
            this.authenticatedOrganization?.teleopHighPingReconnectBehaviors;
        // will be {} after migration
        if (!settings || equal(settings, {})) {
            return teleopHighPingReconnectBehaviorsDefaults;
        } else {
            return settings;
        }
    }

    supportedRegions(): CognitoRegion[] | undefined {
        return this.authenticatedOrganization?.supportedRegions;
    }

    showGetStartedOnOverview(): boolean {
        return !!this.authenticatedOrganization?.showGetStartedOnOverview;
    }

    getInvestigationsEnabled(): boolean {
        return !!this.authenticatedOrganization?.investigationsEnabled;
    }

    getInvestigationsBetaEnabled(): boolean {
        return !!this.authenticatedOrganization?.investigationsBetaEnabled;
    }

    getV2OverviewEnabled(): boolean {
        return !!this.authenticatedOrganization?.v2OverviewEnabled;
    }

    getSidebarV2Enabled(): boolean {
        return !!this.authenticatedOrganization?.sidebarV2Enabled;
    }

    getThemeCustomizerEnabled(): boolean {
        return !!this.authenticatedOrganization?.themeCustomizerEnabled;
    }

    getAiEnabled(): boolean {
        return !!this.authenticatedOrganization?.aiEnabled;
    }

    getTermsModalV2Enabled(): boolean {
        return !!this.authenticatedOrganization?.termsModalV2Enabled;
    }

    deviceConfigurationTemplatesEnabled(): boolean {
        return !!this.authenticatedOrganization
            ?.deviceConfigurationTemplatesEnabled;
    }

    observabilityV1Enabled(): boolean {
        return !!this.authenticatedOrganization?.observabilityV1Enabled;
    }

    analyticsV1Enabled(): boolean {
        return !!this.authenticatedOrganization?.analyticsV1Enabled;
    }

    customRolesEnabled(): boolean {
        return !!this.authenticatedOrganization?.customRolesEnabled;
    }

    mixedModuleModeEnabled(): boolean {
        return !!this.authenticatedOrganization?.mixedModuleModeEnabled;
    }

    newWorkflowsEnabled(): boolean | undefined {
        return (
            this.authenticatedOrganization?.id ===
            "8b824611-7b17-4366-b668-3c97ef1fcf21"
        );
    }

    getTeleop3PreviewButton(): boolean {
        return !!this.authenticatedOrganization?.teleop3PreviewButton;
    }

    getDeviceLocksEnabled(): boolean {
        return !!(
            this.authenticatedOrganization as
                | (IOrganization & { deviceLocksEnabled?: boolean })
                | undefined
        )?.deviceLocksEnabled;
    }

    @computed
    get retentionPeriodStart(): Date {
        const org = this.authenticatedOrganization;
        const daysDataRetained =
            org && org.dataRetentionEnabled ? org.daysDataRetained : undefined;
        const now = new Date();
        return org && daysDataRetained
            ? subDays(now, org.daysDataRetained)
            : subDays(now, 10000);
    }
}
